import { Sponsorstripe } from '@sch-inventory/advertory/sites/aftenposten/index.js';
import React, { useEffect } from 'react';

import type { OptionsFromRequest } from '../../../../core/system/rendering/types.js';
import type {
  FrapiList,
  FrapiListItem,
  FrapiTeaserItem,
} from '../../../types/frapi-types.js';

import Navigation from '../../components/commonComponents/Navigation/Navigation.js';
import Footer from '../../components/commonComponents/Footer.js';
import { trackStoryPageView, withByfunnPulse } from '../../../pulse/index.js';
import { usePulseContext } from '../../../../core/mandatory-integrations/pulse/PulseProvider.js';
import TeaserList from '../../components/teasers/TeaserList.js';
import ListPageDescription from './ListPageDescription.js';
import ListPageFaq from './ListPageFaq.js';
import ListPageRelated from './ListPageRelated.js';
import { buildFaq } from './buildFaq.js';
import Teaser from '../../components/teasers/Teaser.js';
import { ListsProvider } from '../../components/saving/ListsProvider.js';
import { LinkWithPulse } from '../../../../core/mandatory-integrations/pulse/components/LinkWithPulse.js';
import Ad from '../../components/ads/Ad/Ad.js';
import { insertAdsPlacements } from '../../components/ads/Ad/helpers.js';
import useInitializeAds from '../../hooks/useInitAds.js';
import { useSessionData } from '../../../../../public-src/core/js/schibsted-account/react.server.js';

export type ListPageProps = {
  data: FrapiList;
  options: OptionsFromRequest;
};
const ListPage: React.FC<ListPageProps> = ({ data, options }) => {
  const { siteVersion, currentUrl } = options;
  const { properties, items } = data;
  const { faq } = properties;
  const isMobile = siteVersion === 'phone';

  const session = useSessionData();
  const { trackViewListing, registerViewElementTracking, getEnvironmentId } =
    usePulseContext();

  useEffect(() => {
    trackStoryPageView(trackViewListing, currentUrl, 'list page');

    registerViewElementTracking({
      object: {
        page: {
          id: 'list',
          type: 'Listing',
        },
      },
    });
  }, [trackViewListing, currentUrl, data, registerViewElementTracking]);

  useInitializeAds(getEnvironmentId, session, siteVersion);

  const isMasterListPage = items?.every((item) => item.type === 'list');

  const builtFaqs = faq && buildFaq(faq);

  const itemsWithAds = items
    ? insertAdsPlacements<FrapiListItem | FrapiTeaserItem>(
        isMobile,
        items,
        'frontpage',
        'topplister',
      )
    : items;

  return (
    <ListsProvider>
      <div className={`${siteVersion}-front`}>
        <Navigation fullWidth={!isMobile} activeSection="restaurantguiden" />
        <Sponsorstripe placementId="sponsorstripe" />
        <div className="tag-page-container">
          <div className="title-description">
            <h1 className="story-title">{properties.h1}</h1>
            <ListPageDescription description={data.properties.description} />
          </div>
          <div className="story-controls">
            <div className="story-hits">{`${items?.length} ${
              isMasterListPage ? 'lister' : 'anmeldelser'
            }`}</div>
          </div>
          <div className="tag-articles-all">
            {itemsWithAds?.map((item) => {
              if (item.type === 'teaser') {
                return (
                  <Teaser
                    {...item}
                    key={item.id}
                    size={isMobile ? 'big' : 'medium'}
                    mobile={isMobile}
                    fullWidthTeaser={false}
                    lazyLoad={false}
                    colorPalette={undefined}
                    hasGroups={false}
                  />
                );
              }
              if (item.type === 'ad') {
                return (
                  <Ad
                    key={`Ad-${item.id}`}
                    id={item.id}
                    cssClass={item.cssClass}
                    isMobile={isMobile}
                    containerClass={'ad-list'}
                  />
                );
              }

              return (
                <TeaserList
                  {...item}
                  key={item.id}
                  mobile={isMobile}
                  size="medium"
                  slug=""
                />
              );
            })}
          </div>
          <ListPageRelated data={data} options={options} />
          {faq && builtFaqs && (
            <ListPageFaq
              faqs={builtFaqs}
              listId={options.requestParams.childId}
            />
          )}
          <LinkWithPulse
            className="homescreen-button-centered"
            href="/"
            id="redirect-to-homescreen"
            name="Redirect to homescreen"
          >
            Gå til Vink-forsiden
          </LinkWithPulse>
        </div>
        <Footer isMobile={isMobile} />
      </div>
    </ListsProvider>
  );
};

const ListPageWithPulse = withByfunnPulse(ListPage);

export { ListPageWithPulse as ListPage };
